import { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../../utils/format';
import { SESSION_ENDED_EVENT, noteSessionEnded, endedNotice } from '../portal/sessionEnded.js';
import SignOutEverywhere from '../portal/SignOutEverywhere.jsx';

// ── API helpers ───────────────────────────────────────────────────────────────
const casApi = {
  async get(path) {
    const r = await fetch(`/api/resource/${path}`, { credentials: 'include' });
    if (!r.ok) { const e = await r.json().catch(() => ({})); noteSessionEnded(r.status, e); throw new Error(e.error || `Request failed (${r.status})`); }
    return r.json();
  },
  async post(path, body) {
    const r = await fetch(`/api/resource/${path}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); noteSessionEnded(r.status, e); throw new Error(e.error || `Request failed (${r.status})`); }
    return r.json();
  },
  async put(path, body) {
    const r = await fetch(`/api/resource/${path}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); noteSessionEnded(r.status, e); throw new Error(e.error || `Request failed (${r.status})`); }
    return r.json();
  },
};

const clientApi = {
  async get(path) {
    const r = await fetch(`/api/client/${path}`, { credentials: 'include' });
    if (!r.ok) { const e = await r.json().catch(() => ({})); noteSessionEnded(r.status, e); throw new Error(e.error || `Request failed (${r.status})`); }
    return r.json();
  },
  async post(path, body) {
    const r = await fetch(`/api/client/${path}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); noteSessionEnded(r.status, e); throw new Error(e.error || `Request failed (${r.status})`); }
    return r.json();
  },
  async put(path, body) {
    const r = await fetch(`/api/client/${path}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); noteSessionEnded(r.status, e); throw new Error(e.error || `Request failed (${r.status})`); }
    return r.json();
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function toLocalISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function getMondayOf(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return toLocalISO(d);
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toLocalISO(d);
}

function fmtShort(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function statusStyle(status) {
  const m = {
    draft:        { background: '#F3F4F6', color: '#374151' },
    submitted:    { background: '#DBEAFE', color: '#1E40AF' },
    approved:     { background: '#DCFCE7', color: '#166534' },
    rejected:     { background: '#FEE2E2', color: '#DC2626' },
    paid:         { background: '#DCFCE7', color: '#166534' },
    under_review: { background: '#FEF9C3', color: '#854D0E' },
  };
  return m[status] || m.draft;
}

function StatusBadge({ status }) {
  const ss = statusStyle(status);
  return (
    <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, fontWeight: 700, whiteSpace: 'nowrap', ...ss }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const color = pct > 90 ? '#EF4444' : pct > 70 ? '#F59E0B' : '#10B981';
  return (
    <div style={{ background: '#F3F4F6', borderRadius: 4, height: 7, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.6s ease' }} />
    </div>
  );
}

// ── Day selector: 0 / 0.5 / 1 ─────────────────────────────────────────────────
function DaySelector({ value, disabled, onChange }) {
  const opts = [0, 0.5, 1];
  return (
    <div style={{ display: 'inline-flex', border: '1px solid #D1D5DB', borderRadius: 6, overflow: 'hidden' }}>
      {opts.map((opt, idx) => {
        const active = value === opt && opt > 0;
        return (
          <button key={opt} onClick={() => !disabled && onChange(opt)} disabled={disabled}
            style={{
              padding: '5px 9px', fontSize: 11, fontWeight: active ? 700 : 400,
              border: 'none', borderRight: idx < opts.length - 1 ? '1px solid #D1D5DB' : 'none',
              cursor: disabled ? 'default' : 'pointer',
              background: active ? '#0D1D32' : value === opt ? '#F3F4F6' : '#fff',
              color: active ? '#fff' : value === opt ? '#374151' : '#9CA3AF',
              minWidth: 34, transition: 'background 0.1s',
            }}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, portalType = 'resource', notice = '' }) {
  const isClient = portalType === 'client';
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Why they are here without having signed out: the session ran out.
  const [shownNotice, setShownNotice] = useState(notice);
  useEffect(() => { setShownNotice(notice); }, [notice]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setShownNotice('');
    setLoading(true);
    try {
      if (isClient) {
        // Client portal — only try client contact login
        const res = await clientApi.post('login', { email: form.username, password: form.password });
        onLogin({ ...res.contact, userType: 'client', must_change_password: res.must_change_password });
      } else {
        // Resource portal — try worker first, then client contact as fallback
        try {
          const res = await casApi.post('login', { username: form.username, password: form.password });
          onLogin({ ...res.worker, userType: 'worker', must_change_password: res.must_change_password });
          return;
        } catch (workerErr) {
          if (workerErr.message && workerErr.message.toLowerCase().includes('blocked')) {
            setError(workerErr.message);
            return;
          }
        }
        const res = await clientApi.post('login', { email: form.username, password: form.password });
        onLogin({ ...res.contact, userType: 'client', must_change_password: res.must_change_password });
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0D1D32', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif', padding: 24 }}>
      <img src="/av-logo-light.svg" alt="Alton Valley" width="48" height="48" style={{ filter: 'brightness(0) invert(1)', marginBottom: 12 }} />
      <h1 style={{ color: '#E5B87A', fontSize: 18, fontWeight: 800, letterSpacing: 1, margin: '0 0 4px' }}>ALTON VALLEY</h1>
      {isClient && <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginBottom: 32 }}>Client Portal</p>}

      <form onSubmit={submit} style={{ background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0D1D32', marginBottom: 20 }}>Sign In</h2>
        {shownNotice && <div role="status" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 16 }}>{shownNotice}</div>}
        {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 16 }}>{error}</div>}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Email</label>
          <input type="email" autoComplete="email" required value={form.username}
            onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
            style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '8px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Password</label>
          <input type="password" autoComplete="current-password" required value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '8px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <button type="submit" disabled={loading}
          style={{ width: '100%', background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 6, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
        <p style={{ textAlign: 'center', fontSize: 9, color: '#9CA3AF', marginTop: 16 }}>Your access details were provided in your welcome email.</p>
      </form>
    </div>
  );
}

// ── Dashboard View ────────────────────────────────────────────────────────────
function DashboardView({ worker, timesheets, invoices, onNavigate }) {
  const contracts = worker.sows || [];
  const now = new Date();
  const today = now.toISOString().slice(0, 10);

  const greeting = () => {
    const h = now.getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const fmtDay = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Stats
  const pendingTs    = timesheets.filter(ts => ts.status === 'submitted').length;
  const approvedDays = timesheets.filter(ts => ts.status === 'approved')
    .reduce((s, ts) => s + (ts.total_hours || 0) / 8, 0);
  const totalInvoiced = invoices.filter(inv => inv.status !== 'rejected')
    .reduce((s, inv) => s + (inv.total_amount || 0), 0);

  // Combined activity feed — last 10 entries
  const activity = [
    ...timesheets.map(ts => ({
      type: 'timesheet',
      date: ts.submitted_at || ts.created_at || '',
      label: `${fmtShort(ts.week_start)} – ${fmtShort(addDays(ts.week_start, 4))}`,
      sub: `${((ts.total_hours || 0) / 8).toFixed(1)}d`,
      status: ts.status,
    })),
    ...invoices.map(inv => ({
      type: 'invoice',
      date: inv.submitted_at || inv.created_at || '',
      label: inv.invoice_number || 'Invoice',
      sub: `£${(inv.total_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
      status: inv.status,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);

  const fmtMoney = (n) => n >= 10000
    ? `£${(n / 1000).toFixed(1)}k`
    : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div>
      {/* Greeting hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0D1D32 0%, #183352 100%)',
        borderRadius: 14, padding: '22px 24px', marginBottom: 20, color: '#fff',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -24, top: -24, width: 130, height: 130, borderRadius: '50%', background: 'rgba(229,184,122,0.07)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: 30, bottom: -40, width: 90, height: 90, borderRadius: '50%', background: 'rgba(229,184,122,0.04)', pointerEvents: 'none' }} />
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 5, position: 'relative' }}>{fmtDay}</div>
        <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 6, position: 'relative' }}>
          {greeting()}, {worker.first_name}.
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', position: 'relative' }}>
          {contracts.length > 0
            ? `You have ${contracts.length} active contract${contracts.length !== 1 ? 's' : ''} with Alton Valley.`
            : 'Welcome to the Alton Valley portal.'}
        </div>
      </div>

      {/* Contract health cards */}
      {contracts.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Your Contracts</div>
          <div style={{ display: 'grid', gridTemplateColumns: contracts.length === 1 ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {contracts.map(c => {
              const agreed    = parseFloat(c.agreed_days) || 0;
              const logged    = parseFloat(c.days_logged) || 0;
              const invoiced  = parseFloat(c.days_invoiced) || 0;
              const opening   = parseFloat(c.opening_days) || 0;
              // "To invoice" reflects submitted days (incl. pending approval) not yet invoiced.
              const available = Math.max(logged - invoiced, 0);
              const pct       = agreed > 0 ? ((logged + opening) / agreed) * 100 : 0;
              const barColor  = pct > 90 ? '#EF4444' : pct > 70 ? '#F59E0B' : '#10B981';
              const isExpired = c.end_date && c.end_date < today;
              const contractValue = c.day_rate && agreed ? c.day_rate * agreed : null;
              const fmt = n => n % 1 === 0 ? String(n) : n.toFixed(1);

              return (
                <div key={c.id} style={{
                  background: '#fff',
                  border: '1px solid #E5E7EB',
                  borderLeft: `4px solid ${c.is_primary ? '#0D1D32' : '#E5E7EB'}`,
                  borderRadius: 10,
                  padding: '16px',
                }}>
                  {/* Header row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 13, color: '#0D1D32', marginBottom: 1, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{c.reference || 'Contract'}</span>
                        {!!c.is_primary && (
                          <span style={{ fontSize: 8, background: '#EFF6FF', color: '#2563EB', borderRadius: 4, padding: '1px 5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>Primary</span>
                        )}
                      </div>
                      {c.sow_detail && <div style={{ fontSize: 10, color: '#6B7280' }}>{c.sow_detail}</div>}
                      {c.client_name && <div style={{ fontSize: 10, color: '#9CA3AF' }}>{c.client_name}</div>}
                    </div>
                  </div>

                  {/* Timesheet progress */}
                  {agreed > 0 ? (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#9CA3AF', marginBottom: 4 }}>
                        <span>Days submitted</span>
                        <span style={{ fontWeight: 600, color: pct > 90 ? '#EF4444' : '#6B7280' }}>{fmt(logged)} / {fmt(agreed)}</span>
                      </div>
                      <ProgressBar value={logged} max={agreed} />
                    </div>
                  ) : (
                    <div style={{ fontSize: 10, color: '#D1D5DB', marginBottom: 10, fontStyle: 'italic' }}>No day budget set</div>
                  )}

                  {/* 4-metric summary grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10, paddingTop: 8, borderTop: '1px solid #F3F4F6' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: '#0D1D32' }}>{agreed > 0 ? fmt(agreed) : '—'}</div>
                      <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 2, lineHeight: 1.3 }}>Allocated</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: logged > 0 ? '#2563EB' : '#D1D5DB' }}>{logged > 0 ? fmt(logged) : '—'}</div>
                      <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 2, lineHeight: 1.3 }}>Submitted</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: invoiced > 0 ? '#A1723D' : '#D1D5DB' }}>{invoiced > 0 ? fmt(invoiced) : '—'}</div>
                      <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 2, lineHeight: 1.3 }}>Invoiced</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: available > 0 ? '#16A34A' : '#D1D5DB' }}>{available > 0 ? fmt(available) : '—'}</div>
                      <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 2, lineHeight: 1.3 }}>To Invoice</div>
                    </div>
                  </div>

                  {/* Footer row */}
                  <div style={{ paddingTop: 8, borderTop: '1px solid #F9FAFB' }}>
                    <div style={{ fontSize: 9, color: '#9CA3AF' }}>
                      {c.start_date ? fmtShort(c.start_date) : ''}
                      {c.start_date && c.end_date ? ' → ' : ''}
                      {c.end_date ? fmtShort(c.end_date) : ''}
                      {isExpired && <span style={{ color: '#EF4444', fontWeight: 700, marginLeft: 6 }}>Ended</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        <div style={{
          background: pendingTs > 0 ? '#FFF7ED' : '#fff',
          border: `1px solid ${pendingTs > 0 ? '#FED7AA' : '#E5E7EB'}`,
          borderRadius: 10, padding: '14px 10px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: pendingTs > 0 ? '#EA580C' : '#D1D5DB', lineHeight: 1 }}>{pendingTs}</div>
          <div style={{ fontSize: 8, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 5 }}>Awaiting approval</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: approvedDays > 0 ? '#166534' : '#D1D5DB', lineHeight: 1 }}>
            {approvedDays % 1 === 0 ? approvedDays : approvedDays.toFixed(1)}
          </div>
          <div style={{ fontSize: 8, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 5 }}>Days approved</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: totalInvoiced >= 10000 ? 20 : 24, fontWeight: 900, color: totalInvoiced > 0 ? '#A1723D' : '#D1D5DB', lineHeight: 1 }}>
            {totalInvoiced > 0 ? fmtMoney(totalInvoiced) : '£0'}
          </div>
          <div style={{ fontSize: 8, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 5 }}>Total invoiced</div>
        </div>
      </div>

      {/* No contracts hint */}
      {contracts.length === 0 && (
        <div style={{ background: '#F9FAFB', border: '1px dashed #D1D5DB', borderRadius: 10, padding: '20px', textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>📋</div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 4 }}>No contracts linked yet</div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>Your contracts will appear here once Alton Valley has set them up.</div>
        </div>
      )}

      {/* Recent activity */}
      {activity.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8 }}>Recent Activity</div>
          </div>
          {activity.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '9px 14px', borderTop: i > 0 ? '1px solid #F9FAFB' : 'none', gap: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                background: a.type === 'invoice' ? '#FFF7ED' : '#EFF6FF',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
              }}>
                {a.type === 'invoice' ? '🧾' : '📅'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1D32', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.label}</div>
                <div style={{ fontSize: 10, color: '#9CA3AF' }}>{a.sub}</div>
              </div>
              <StatusBadge status={a.status} />
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button onClick={() => onNavigate('timesheet')}
          style={{ padding: '13px', background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
          <span>📅</span> Log Timesheet
        </button>
        <button onClick={() => onNavigate('invoice')}
          style={{ padding: '13px', background: '#A1723D', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
          <span>🧾</span> Submit Invoice
        </button>
      </div>
    </div>
  );
}

// ── Timesheet View ────────────────────────────────────────────────────────────
function TimesheetView({ worker, onRefresh }) {
  const contracts = worker.sows || [];
  const [weekStart, setWeekStart] = useState(getMondayOf());
  const [entries, setEntries] = useState({});
  const [notes, setNotes] = useState('');
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  // Which contract-days were worked as an evening, keyed like `entries`.
  const [evenings, setEvenings] = useState({});
  const toggleEvening = (cId, day) => setEvenings(e => ({ ...e, [`${cId}:${day}`]: !e[`${cId}:${day}`] }));

  const currentWeek = getMondayOf();
  const isFuture   = weekStart > currentWeek;
  const weekDates  = DAYS.map((_, i) => addDays(weekStart, i));
  const getEntry   = (cId, day) => entries[`${cId}:${day}`] || 0;
  const setEntry   = (cId, day, val) => setEntries(e => ({ ...e, [`${cId}:${day}`]: val }));
  const dayTotal   = (day) => contracts.reduce((s, c) => s + getEntry(c.id, day), 0);
  const contractTotal = (cId) => DAYS.reduce((s, d) => s + getEntry(cId, d), 0);
  const grandTotal = DAYS.reduce((s, d) => s + dayTotal(d), 0);
  const primaryContract = contracts.find(c => c.is_primary) || contracts[0];
  const approverName = primaryContract?.approver_name || primaryContract?.owner_name;

  const load = useCallback(async () => {
    setLoading(true); setMsg(''); setSubmitSuccess(false);
    try {
      const ts = await casApi.get(`timesheets/${weekStart}`);
      setPrefilled(false);
      if (ts?.plow_prefill) {
        // No timesheet yet for this week: start from the PLOW days instead of blank.
        setExisting(null); setNotes('');
        const map = {}, eve = {};
        for (const e of ts.plow_prefill) { map[`${e.contract_id}:${e.day}`] = e.days; eve[`${e.contract_id}:${e.day}`] = !!e.evening; }
        setEntries(map); setEvenings(eve);
        setPrefilled(true);
      } else if (ts) {
        setExisting(ts);
        const map = {}, eve = {};
        for (const e of (ts.entries || [])) { map[`${e.contract_id}:${e.day_of_week}`] = e.days; eve[`${e.contract_id}:${e.day_of_week}`] = !!e.is_evening; }
        setEntries(map); setEvenings(eve);
        setNotes(ts.notes || '');
      } else {
        setExisting(null); setEntries({}); setEvenings({}); setNotes('');
      }
    } finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const save = async (action) => {
    setSaving(true); setMsg('');
    try {
      const entriesArr = Object.entries(entries)
        .filter(([, days]) => days > 0)
        .map(([key, days]) => {
          const idx = key.indexOf(':');
          return { contract_id: key.slice(0, idx), day: key.slice(idx + 1), days, evening: !!evenings[key] };
        });
      await casApi.post('timesheets', { week_start: weekStart, notes, action, entries: entriesArr });
      if (action === 'submit') {
        setSubmitSuccess(true);
        setMsg(approverName ? `Week submitted — it will be reviewed by ${approverName}.` : 'Week submitted successfully.');
        onRefresh?.();
      } else {
        setMsg('Draft saved.');
      }
      load();
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally { setSaving(false); }
  };

  const isReadOnly = existing?.status === 'approved' || isFuture;

  const resetWeek = async () => {
    if (!window.confirm('Clear all entries for this week and start again?')) return;
    setEntries({}); setEvenings({}); setNotes(''); setMsg(''); setPrefilled(false);
    if (existing) {
      setSaving(true);
      try {
        await casApi.post('timesheets', { week_start: weekStart, notes: '', action: 'draft', entries: [] });
        load();
      } catch (err) { setMsg(`Error: ${err.message}`); }
      finally { setSaving(false); }
    }
  };

  if (contracts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
        <div style={{ fontSize: 20, marginBottom: 10 }}>📋</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>No active contracts found.</div>
        <div style={{ fontSize: 11, marginTop: 4 }}>Please contact Alton Valley to set up your contract.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Week navigator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 12px', marginBottom: 20 }}>
        <button onClick={() => setWeekStart(w => addDays(w, -7))}
          style={{ padding: '6px 14px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#374151', fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>‹</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: 700, color: '#0D1D32', fontSize: 13 }}>
            {fmtShort(weekStart)} – {fmtShort(addDays(weekStart, 6))} {new Date(weekStart + 'T00:00:00').getFullYear()}
            {weekStart === currentWeek && <span style={{ fontSize: 9, marginLeft: 6, color: '#6B7280', fontWeight: 600 }}>This week</span>}
          </div>
          {isFuture && (
            <div style={{ marginTop: 3, fontSize: 10, color: '#D97706', fontWeight: 600 }}>⚠ Future week — timesheets cannot be submitted in advance</div>
          )}
          {!isFuture && existing && (
            <div style={{ marginTop: 3 }}>
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600, ...statusStyle(existing.status) }}>
                {existing.status.charAt(0).toUpperCase() + existing.status.slice(1)}
                {existing.approved_by_name && existing.status === 'approved' ? ` · ${existing.approved_by_name}` : ''}
              </span>
            </div>
          )}
        </div>
        <button onClick={() => setWeekStart(w => addDays(w, 7))} disabled={weekStart >= currentWeek}
          style={{ padding: '6px 14px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, cursor: weekStart >= currentWeek ? 'not-allowed' : 'pointer', fontSize: 18, color: weekStart >= currentWeek ? '#D1D5DB' : '#374151', fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>›</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#9CA3AF', padding: 24 }}>Loading…</div>
      ) : (
        <div>
          <div style={{ marginBottom: 16 }}>
            {/* Contract header(s) */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, paddingBottom: 10, borderBottom: '2px solid #E5E7EB' }}>
              {contracts.map(c => (
                <div key={c.id} style={{ background: '#F3F4F6', borderRadius: 6, padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#0D1D32' }}>{c.reference || 'Contract'}</span>
                </div>
              ))}
            </div>

            {prefilled && !isReadOnly && (
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, padding: '7px 12px', marginBottom: 10, fontSize: 11, color: '#1E40AF' }}>
                Pre-filled from the PLOW. Check the days, then save or submit the week.
              </div>
            )}

            {/* Day rows */}
            {DAYS.map((day, i) => {
              const dTotal = dayTotal(day);
              const isWeekend = i >= 5;
              return (
                <div key={day} style={{ display: 'flex', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #F3F4F6', background: isWeekend ? '#FAFAFA' : '#fff', gap: 10 }}>
                  <div style={{ minWidth: 64 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: isWeekend ? '#9CA3AF' : '#0D1D32' }}>{DAY_LABELS[i]}</div>
                    <div style={{ fontSize: 10, color: '#9CA3AF' }}>{fmtShort(weekDates[i])}</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-start', alignItems: 'center' }}>
                    {contracts.map((c, ci) => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {contracts.length > 1 && (
                          <span style={{ fontSize: 9, color: '#6B7280', fontWeight: 700, whiteSpace: 'nowrap' }}>{c.reference || `C${ci + 1}`}</span>
                        )}
                        <DaySelector value={getEntry(c.id, day)} disabled={isReadOnly} onChange={v => setEntry(c.id, day, v)} />
                        {getEntry(c.id, day) > 0 && (() => {
                          const on = !!evenings[`${c.id}:${day}`];
                          return (
                            <button onClick={() => !isReadOnly && toggleEvening(c.id, day)} disabled={isReadOnly}
                              aria-pressed={on} title="Worked as an evening"
                              style={{ padding: '5px 8px', fontSize: 10, fontWeight: on ? 700 : 500, borderRadius: 6, cursor: isReadOnly ? 'default' : 'pointer',
                                border: `1px solid ${on ? '#A1723D' : '#D1D5DB'}`, background: on ? '#FDF6EC' : '#fff', color: on ? '#A1723D' : '#6B7280' }}>
                              🌙 Evening
                            </button>
                          );
                        })()}
                      </div>
                    ))}
                    {contracts.length > 1 && (
                      <div style={{ minWidth: 32, textAlign: 'left', fontWeight: 700, fontSize: 12, color: dTotal > 0 ? '#0D1D32' : '#D1D5DB' }}>
                        {dTotal > 0 ? `${dTotal}d` : '—'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Totals row */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderTop: '2px solid #0D1D32', gap: 10 }}>
              <div style={{ minWidth: 64, fontWeight: 700, fontSize: 13, color: '#0D1D32' }}>Total</div>
              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-start', alignItems: 'center' }}>
                {contracts.map((c, ci) => {
                  const cTotal = contractTotal(c.id);
                  return (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {contracts.length > 1 && (
                        <span style={{ fontSize: 9, color: '#6B7280', fontWeight: 700 }}>{c.reference || `C${ci + 1}`}</span>
                      )}
                      <span style={{ fontWeight: 700, fontSize: 13, color: cTotal > 0 ? '#0D1D32' : '#D1D5DB' }}>{cTotal > 0 ? `${cTotal}d` : '—'}</span>
                    </div>
                  );
                })}
                {contracts.length > 1 && (
                  <div style={{ minWidth: 32, textAlign: 'left', fontWeight: 800, fontSize: 15, color: '#0D1D32' }}>{grandTotal}d</div>
                )}
              </div>
            </div>
          </div>

          {!isReadOnly && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '8px', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          )}

          {existing?.rejection_reason && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#DC2626' }}>
              <strong>Rejected:</strong> {existing.rejection_reason}
            </div>
          )}

          {msg && (
            <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 6, fontSize: 12, background: msg.startsWith('Error') ? '#FEF2F2' : '#F0FDF4', color: msg.startsWith('Error') ? '#DC2626' : '#166534' }}>
              {msg}
            </div>
          )}

          {!isReadOnly && (
            <div>
              {existing?.status === 'submitted' && (
                <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 6, padding: '7px 12px', marginBottom: 10, fontSize: 11, color: '#92400E' }}>
                  ✏️ Submitted but not yet approved — you can still update it.
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={resetWeek} disabled={saving}
                  style={{ padding: '10px 14px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#6B7280', whiteSpace: 'nowrap' }}
                  title="Clear all entries and start the week again">
                  ↺ Reset
                </button>
                <button onClick={() => save('draft')} disabled={saving}
                  style={{ flex: 1, padding: '10px', background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Save Draft
                </button>
                <button onClick={() => save('submit')} disabled={saving || grandTotal === 0}
                  style={{ flex: 2, padding: '10px', background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (saving || grandTotal === 0) ? 0.5 : 1 }}>
                  {saving ? 'Saving…' : existing?.status === 'submitted' ? 'Resubmit Week' : 'Submit Week'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Invoice View ──────────────────────────────────────────────────────────────
function InvoiceView({ worker, invoices, onRefresh }) {
  const today = new Date().toISOString().slice(0, 10);
  const contracts = worker.sows || [];
  const primarySow = contracts.find(s => s.is_primary) || contracts[0] || null;

  const [showForm, setShowForm] = useState(invoices.length === 0);
  const [form, setForm] = useState({
    contract_id: primarySow?.id || '',
    invoice_number: '',
    invoice_date: today,
    period_from: getMondayOf(new Date(Date.now() - 7 * 86400000)),
    period_to: addDays(getMondayOf(new Date(Date.now() - 7 * 86400000)), 6),
    labour_days: '',
    day_rate: primarySow?.day_rate || '',
    notes: '',
  });
  const [lines, setLines] = useState([]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectContract = (contractId) => {
    setForm(f => ({ ...f, contract_id: contractId }));
  };

  const labourTotal  = (parseFloat(form.labour_days) || 0) * (parseFloat(form.day_rate) || 0);
  const expensesTotal = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const total = labourTotal + expensesTotal;

  const selectedContract = contracts.find(x => x.id === form.contract_id);
  // Invoiceable basis = days SUBMITTED (incl. those still pending approval) less
  // days already invoiced. Contractors can invoice as soon as they've submitted
  // their timesheet; approval can follow a day or two later.
  const availableDays = selectedContract
    ? Math.max((parseFloat(selectedContract.days_logged) || 0) - (parseFloat(selectedContract.days_invoiced) || 0), 0)
    : null;
  const contractBlocked = form.contract_id !== '' && availableDays !== null && availableDays === 0;

  const addLine    = () => setLines(l => [...l, { description: '', amount: '' }]);
  const removeLine = (i) => setLines(l => l.filter((_, idx) => idx !== i));
  const setLine    = (i, k, v) => setLines(l => l.map((line, idx) => idx === i ? { ...line, [k]: v } : line));
  const handleFile = (e) => { const f = e.target.files[0]; if (f) setFile(f); };

  const submit = async () => {
    if (contractBlocked) { setMsg('Error: No submitted timesheet days available to invoice on this contract. Submit your timesheet for those days first.'); return; }
    if (form.contract_id && availableDays !== null && parseFloat(form.labour_days) > availableDays) {
      setMsg(`Error: Labour days (${form.labour_days}) exceed your submitted days available to invoice (${availableDays % 1 === 0 ? availableDays : availableDays.toFixed(1)}).`);
      return;
    }
    if (!form.invoice_number) { setMsg('Error: Invoice number is required'); return; }
    if (!file) { setMsg('Error: Please attach your invoice document.'); return; }
    setSaving(true); setMsg('');
    try {
      let file_url = null, file_key = null;
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const upload = await casApi.post('upload', { filename: file.name, content_type: file.type, data: btoa(binary) });
      file_url = upload.url; file_key = upload.key;

      await casApi.post('invoices', {
        ...form,
        contract_id: form.contract_id || null,
        labour_days: parseFloat(form.labour_days) || null,
        day_rate: parseFloat(form.day_rate) || null,
        labour_total: labourTotal || null,
        expenses_total: expensesTotal,
        total_amount: total || null,
        total_incl_vat: total || null,
        file_url, file_key,
        lines: lines.filter(l => l.description && l.amount).map(l => ({
          line_type: 'expense', description: l.description, amount: parseFloat(l.amount),
        })),
      });
      setDone(true);
      onRefresh?.();
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 20px' }}>
        <div style={{ width: 60, height: 60, background: '#DCFCE7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 26 }}>✓</div>
        <div style={{ fontWeight: 800, fontSize: 17, color: '#166534', marginBottom: 8 }}>Invoice Submitted</div>
        <p style={{ fontSize: 12, color: '#6B7280', maxWidth: 280, margin: '0 auto 24px' }}>
          Alton Valley Finance will review it shortly. You'll see it in your invoice history below once we've processed it.
        </p>
        <button onClick={() => { setDone(false); setMsg(''); setForm(f => ({ ...f, invoice_number: '' })); setLines([]); setFile(null); setShowForm(false); }}
          style={{ background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Back to Invoices
        </button>
      </div>
    );
  }

  const INP = { width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '7px 8px', fontSize: 12, boxSizing: 'border-box' };

  return (
    <div>
      {/* Invoice history */}
      {invoices.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8 }}>Invoice History</div>
            <div style={{ fontSize: 10, color: '#9CA3AF' }}>{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</div>
          </div>
          {invoices.slice(0, 6).map((inv, i) => {
            const ss = statusStyle(inv.status);
            return (
              <div key={inv.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderTop: i > 0 ? '1px solid #F9FAFB' : 'none', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#0D1D32' }}>{inv.invoice_number || 'Invoice'}</div>
                  {inv.period_from && (
                    <div style={{ fontSize: 10, color: '#9CA3AF' }}>{formatDate(inv.period_from)} – {formatDate(inv.period_to)}</div>
                  )}
                </div>
                <div style={{ fontWeight: 800, fontSize: 13, color: '#A1723D', marginRight: 6, flexShrink: 0 }}>
                  £{(inv.total_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
                <StatusBadge status={inv.status} />
              </div>
            );
          })}
          {invoices.length > 6 && (
            <div style={{ padding: '8px 14px', fontSize: 10, color: '#9CA3AF', textAlign: 'center', background: '#FAFAFA', borderTop: '1px solid #F3F4F6' }}>
              + {invoices.length - 6} more in History tab
            </div>
          )}
        </div>
      )}

      {/* Submit toggle / form */}
      {!showForm ? (
        <button onClick={() => setShowForm(true)}
          style={{ width: '100%', padding: '14px', background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span>🧾</span> Submit New Invoice
        </button>
      ) : (
        <div>
          {invoices.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0D1D32' }}>New Invoice</div>
              <button onClick={() => { setShowForm(false); setMsg(''); }}
                style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            </div>
          )}

          <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 11, color: '#0369A1' }}>
            Attach a PDF or image copy of your invoice — this is required for our Finance team to process payment.
          </div>

          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 11, color: '#92400E' }}>
            <strong>All figures entered below are excluding VAT.</strong> Your attached invoice should add VAT on top where applicable to your contracting company.
          </div>

          {/* Contract selector */}
          {contracts.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Contract *</label>
              <select value={form.contract_id} onChange={e => selectContract(e.target.value)} style={{ ...INP, appearance: 'none' }}>
                <option value="">— Select contract —</option>
                {contracts.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.reference || 'Contract'}{c.client_name ? ` · ${c.client_name}` : ''}
                  </option>
                ))}
              </select>
              {form.contract_id && availableDays !== null && (
                availableDays === 0 ? (
                  <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '8px 12px', marginTop: 6, fontSize: 11, color: '#DC2626', fontWeight: 600 }}>
                    No submitted timesheet days available to invoice on this contract. Submit your timesheet for those days first.
                  </div>
                ) : (
                  <div style={{ fontSize: 10, color: '#16A34A', marginTop: 4, fontWeight: 600 }}>
                    {availableDays % 1 === 0 ? availableDays : availableDays.toFixed(1)}d submitted and ready to invoice on this contract
                  </div>
                )
              )}
            </div>
          )}

          {!contractBlocked && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Invoice Number *</label>
                  <input type="text" required value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} style={INP} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Invoice Date</label>
                  <input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} style={INP} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Period From</label>
                  <input type="date" value={form.period_from} onChange={e => set('period_from', e.target.value)} style={INP} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Period To</label>
                  <input type="date" value={form.period_to} onChange={e => set('period_to', e.target.value)} style={INP} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>
                    Days Worked
                    {availableDays !== null && <span style={{ fontWeight: 400, color: '#6B7280' }}> (max {availableDays % 1 === 0 ? availableDays : availableDays.toFixed(1)}d)</span>}
                  </label>
                  <input type="number" min="0" step="0.5" max={availableDays !== null ? availableDays : undefined} value={form.labour_days} onChange={e => set('labour_days', e.target.value)} style={INP} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Day Rate (£)</label>
                  <input type="number" min="0" step="0.01" value={form.day_rate} onChange={e => set('day_rate', e.target.value)} style={INP} />
                </div>
              </div>

              {labourTotal > 0 && (
                <div style={{ background: '#F9FAFB', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B7280' }}>Labour subtotal</span>
                  <span style={{ fontWeight: 700, color: '#0D1D32' }}>£{labourTotal.toFixed(2)}</span>
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Expenses</label>
                  <button onClick={addLine} style={{ fontSize: 10, background: 'none', border: '1px dashed #D1D5DB', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: '#6B7280' }}>+ Add</button>
                </div>
                {lines.map((line, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                    <input type="text" placeholder="Description" value={line.description} onChange={e => setLine(i, 'description', e.target.value)}
                      style={{ flex: 1, border: '1px solid #D1D5DB', borderRadius: 6, padding: '6px 8px', fontSize: 11 }} />
                    <input type="number" placeholder="£" min="0" step="0.01" value={line.amount} onChange={e => setLine(i, 'amount', e.target.value)}
                      style={{ width: 80, border: '1px solid #D1D5DB', borderRadius: 6, padding: '6px 8px', fontSize: 11 }} />
                    <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14 }}>✕</button>
                  </div>
                ))}
              </div>

              <div style={{ background: '#0D1D32', color: '#fff', borderRadius: 8, padding: '12px 16px', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, opacity: 0.6 }}>Invoice Total <span style={{ fontSize: 10 }}>(excl. VAT)</span></span>
                <span style={{ fontSize: 20, fontWeight: 900, color: '#E5B87A' }}>£{total.toFixed(2)}</span>
              </div>
              <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 14, textAlign: 'right' }}>Add VAT on your attached invoice if applicable.</div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
                  style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '7px 8px', fontSize: 12, boxSizing: 'border-box', resize: 'vertical' }} />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Invoice Document <span style={{ color: '#DC2626', fontWeight: 700 }}>* required</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, border: `2px dashed ${file ? '#86EFAC' : '#D1D5DB'}`, borderRadius: 8, padding: '16px', cursor: 'pointer', background: file ? '#F0FDF4' : '#FAFAFA' }}>
                  <span style={{ fontSize: 22 }}>{file ? '📎' : '📄'}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{file ? file.name : 'Click to upload'}</div>
                    {file
                      ? <div style={{ fontSize: 10, color: '#16A34A' }}>{(file.size / 1024).toFixed(0)} KB — ready to submit</div>
                      : <div style={{ fontSize: 10, color: '#9CA3AF' }}>PDF, JPG or PNG</div>}
                  </div>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} style={{ display: 'none' }} />
                </label>
              </div>

              {msg && (
                <div style={{ padding: '8px 12px', background: msg.startsWith('Error') ? '#FEF2F2' : '#F0FDF4', borderRadius: 6, fontSize: 12, color: msg.startsWith('Error') ? '#DC2626' : '#166534', marginBottom: 12 }}>
                  {msg}
                </div>
              )}

              <button onClick={submit} disabled={saving}
                style={{ width: '100%', padding: '13px', background: saving ? '#6B7280' : '#A1723D', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 0.15s' }}>
                {saving ? 'Submitting…' : 'Submit Invoice'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── History View ──────────────────────────────────────────────────────────────
function HistoryView({ timesheets, invoices }) {
  const [tab, setTab] = useState('timesheets');

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['timesheets', 'invoices'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ flex: 1, padding: '7px', fontSize: 11, fontWeight: 700, borderRadius: 6, border: '1px solid', cursor: 'pointer',
              background: tab === t ? '#0D1D32' : '#F3F4F6',
              color: tab === t ? '#fff' : '#374151',
              borderColor: tab === t ? '#0D1D32' : '#E5E7EB' }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'timesheets' ? (
        <div>
          {timesheets.length === 0 && <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, paddingTop: 20 }}>No timesheets yet.</p>}
          {timesheets.map(ts => {
            const totalDays = (ts.total_hours || 0) / 8;

            // Build per-contract breakdown from enriched entries
            const byContract = {};
            for (const e of (ts.entries || [])) {
              const key = e.contract_ref || e.contract_id || '(no ref)';
              if (!byContract[key]) byContract[key] = { ref: e.contract_ref || key, sow: e.sow_ref || '', days: {} };
              byContract[key].days[e.day_of_week] = (byContract[key].days[e.day_of_week] || 0) + e.days;
            }
            const contractRows = Object.values(byContract);
            const hasSow = contractRows.some(r => r.sow);

            return (
              <div key={ts.id} style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, color: '#0D1D32', fontSize: 13 }}>
                    {fmtShort(ts.week_start)} – {fmtShort(addDays(ts.week_start, 4))}
                  </span>
                  <StatusBadge status={ts.status} />
                </div>
                {ts.status === 'submitted' && (
                  <div style={{ fontSize: 10, color: '#2563EB', marginBottom: 8 }}>
                    Submitted {ts.submitted_at ? new Date(ts.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    {ts.pending_approver_name ? ` · awaiting ${ts.pending_approver_name}` : ''}
                  </div>
                )}
                {ts.status === 'approved' && ts.approved_by_name && (
                  <div style={{ fontSize: 10, color: '#16A34A', marginBottom: 8 }}>Approved by {ts.approved_by_name}</div>
                )}
                {ts.status === 'rejected' && (
                  <div style={{ marginBottom: 8, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '6px 10px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#DC2626' }}>
                      Rejected{ts.rejected_by_name ? ` by ${ts.rejected_by_name}` : ''}
                      {ts.rejected_at ? ` · ${new Date(ts.rejected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                    </div>
                    {ts.rejection_reason && <div style={{ fontSize: 10, color: '#7F1D1D', marginTop: 3 }}>{ts.rejection_reason}</div>}
                  </div>
                )}

                {contractRows.length > 0 ? (
                  <div style={{ overflowX: 'auto', marginTop: 6 }}>
                    <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', color: '#9CA3AF', fontWeight: 600, padding: '2px 8px 4px 0', whiteSpace: 'nowrap' }}>Contract</th>
                          {hasSow && <th style={{ textAlign: 'left', color: '#9CA3AF', fontWeight: 600, padding: '2px 8px 4px', whiteSpace: 'nowrap' }}>SOW</th>}
                          {DAY_LABELS.map(d => (
                            <th key={d} style={{ textAlign: 'center', color: '#9CA3AF', fontWeight: 600, padding: '2px 4px 4px', minWidth: 28 }}>{d}</th>
                          ))}
                          <th style={{ textAlign: 'right', color: '#9CA3AF', fontWeight: 600, padding: '2px 0 4px 8px', whiteSpace: 'nowrap' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contractRows.map((row, ri) => {
                          const rowTotal = DAYS.reduce((s, d) => s + (row.days[d] || 0), 0);
                          return (
                            <tr key={ri} style={{ borderTop: '1px solid #F3F4F6' }}>
                              <td style={{ padding: '3px 8px 3px 0', fontWeight: 700, color: '#0D1D32', whiteSpace: 'nowrap' }}>{row.ref}</td>
                              {hasSow && <td style={{ padding: '3px 8px', color: '#6B7280', whiteSpace: 'nowrap' }}>{row.sow || '—'}</td>}
                              {DAYS.map(d => {
                                const v = row.days[d] || 0;
                                return (
                                  <td key={d} style={{ textAlign: 'center', padding: '3px 4px', fontWeight: v > 0 ? 700 : 400, color: v > 0 ? '#0D1D32' : '#D1D5DB' }}>
                                    {v > 0 ? v : 0}
                                  </td>
                                );
                              })}
                              <td style={{ textAlign: 'right', padding: '3px 0 3px 8px', fontWeight: 700, color: rowTotal > 0 ? '#0D1D32' : '#D1D5DB' }}>
                                {rowTotal > 0 ? `${rowTotal}d` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                        {contractRows.length > 1 && (
                          <tr style={{ borderTop: '2px solid #0D1D32' }}>
                            <td colSpan={hasSow ? 2 : 1} style={{ padding: '3px 8px 3px 0', fontWeight: 700, color: '#0D1D32' }}>Total</td>
                            {DAYS.map(d => {
                              const dayT = contractRows.reduce((s, r) => s + (r.days[d] || 0), 0);
                              return (
                                <td key={d} style={{ textAlign: 'center', padding: '3px 4px', fontWeight: 700, color: dayT > 0 ? '#0D1D32' : '#D1D5DB' }}>
                                  {dayT > 0 ? dayT : 0}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: 'right', padding: '3px 0 3px 8px', fontWeight: 800, color: '#0D1D32' }}>{totalDays % 1 === 0 ? totalDays : totalDays.toFixed(1)}d</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                    {DAYS.map((d, i) => {
                      const days = (ts[`${d}_hours`] || 0) / 8;
                      return (
                        <div key={d} style={{ textAlign: 'center', minWidth: 28 }}>
                          <div style={{ fontSize: 8, color: '#9CA3AF' }}>{DAY_LABELS[i]}</div>
                          <div style={{ fontSize: 12, fontWeight: days > 0 ? 700 : 400, color: days > 0 ? '#0D1D32' : '#9CA3AF' }}>{days}</div>
                        </div>
                      );
                    })}
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                      <div style={{ fontSize: 8, color: '#9CA3AF' }}>Total</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1D32' }}>{totalDays.toFixed(1)}d</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          {invoices.length === 0 && <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, paddingTop: 20 }}>No invoices submitted yet.</p>}
          {invoices.map(inv => (
            <div key={inv.id} style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#0D1D32', fontSize: 13 }}>{inv.invoice_number || 'Invoice'}</div>
                  {inv.period_from && <div style={{ fontSize: 10, color: '#6B7280' }}>{formatDate(inv.period_from)} – {formatDate(inv.period_to)}</div>}
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#A1723D', marginTop: 4 }}>
                    £{(inv.total_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <StatusBadge status={inv.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Account View ──────────────────────────────────────────────────────────────
function AccountView({ worker, onSignedOutEverywhere }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [ok, setOk] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setMsg(''); setOk(false); };

  const CRITERIA = [
    { label: 'At least 8 characters',        test: p => p.length >= 8 },
    { label: 'At least one uppercase letter', test: p => /[A-Z]/.test(p) },
    { label: 'At least one number',           test: p => /[0-9]/.test(p) },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) { setMsg('New passwords do not match.'); return; }
    const failing = CRITERIA.find(c => !c.test(form.new_password));
    if (failing) { setMsg(failing.label + ' — requirement not met.'); return; }
    setSaving(true); setMsg('');
    try {
      await casApi.post('change-password', { current_password: form.current_password, new_password: form.new_password });
      setOk(true);
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setMsg(err.message || 'Failed to change password.');
    } finally { setSaving(false); }
  };

  const INP = { width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' };
  const LBL = { display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 };

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', padding: 20, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1D32', marginBottom: 3 }}>{worker.first_name} {worker.last_name}</div>
        <div style={{ fontSize: 11, color: '#6B7280' }}>{worker.email || worker.username}</div>
        {worker.phone && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{worker.phone}</div>}
      </div>

      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1D32', marginBottom: 16 }}>Change Password</div>
        {ok && <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: '#166534', marginBottom: 16 }}>✓ Password changed successfully.</div>}
        {msg && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: '#DC2626', marginBottom: 16 }}>{msg}</div>}

        <form onSubmit={submit}>
          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Current Password</label>
            <input type="password" required autoComplete="current-password" value={form.current_password} onChange={e => set('current_password', e.target.value)} style={INP} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>New Password</label>
            <input type="password" required autoComplete="new-password" value={form.new_password} onChange={e => set('new_password', e.target.value)} style={INP} />
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {CRITERIA.map(c => {
                const met = form.new_password ? c.test(form.new_password) : null;
                return (
                  <div key={c.label} style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 5, color: met === null ? '#9CA3AF' : met ? '#16A34A' : '#DC2626' }}>
                    <span>{met === null ? '○' : met ? '✓' : '✗'}</span> {c.label}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={LBL}>Confirm New Password</label>
            <input type="password" required autoComplete="new-password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} style={INP} />
            {form.confirm_password && form.new_password !== form.confirm_password && (
              <div style={{ fontSize: 10, color: '#DC2626', marginTop: 4 }}>Passwords do not match.</div>
            )}
          </div>
          <button type="submit" disabled={saving}
            style={{ width: '100%', background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 6, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Pen test AD26341, AD8: end the sessions this login has on other devices. */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-3.5">
        <div className="text-[13px] font-bold text-navy mb-1">Signed in on other devices</div>
        <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">
          Signed in somewhere you no longer use, or think somebody else could be using your account? This signs you out
          on every computer, phone and browser, this one included. Changing your password above also signs out every
          other device.
        </p>
        <SignOutEverywhere endpoint="resource/logout-all" onSignedOut={onSignedOutEverywhere}
          className="border border-gray-200 bg-white text-navy rounded-md px-4 py-2 text-[12px] font-bold hover:bg-gray-50" />
      </div>
    </div>
  );
}

// ── Change Password Screen (forced on first login, optional thereafter) ───────
function ChangePasswordScreen({ contact, onDone, forced = false, portalType = 'client' }) {
  const api = portalType === 'client' ? clientApi : casApi;
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [err, setErr]   = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (form.next !== form.confirm) { setErr('New passwords do not match.'); return; }
    setSaving(true);
    try {
      await api.post('change-password', { current_password: form.current, new_password: form.next });
      onDone();
    } catch (e) { setErr(e.message || 'Failed to change password'); }
    setSaving(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/av-logo-light.svg" alt="Alton Valley" width="40" height="40" style={{ filter: 'brightness(0)', marginBottom: 10, opacity: 0.15 }} />
          <div style={{ color: '#0D1D32', fontSize: 18, fontWeight: 800, letterSpacing: 0.5 }}>Set Your Password</div>
          {forced && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>Please set a personal password before continuing.</div>}
        </div>
        <form onSubmit={submit} style={{ background: '#fff', borderRadius: 12, padding: 28, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {forced && (
            <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#92400E', marginBottom: 20 }}>
              You're signed in with a temporary password. Set a new one to continue.
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>CURRENT / TEMPORARY PASSWORD</label>
            <input type="password" value={form.current} onChange={e => setForm(f => ({ ...f, current: e.target.value }))} required
              style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '9px 12px', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>NEW PASSWORD</label>
            <input type="password" value={form.next} onChange={e => setForm(f => ({ ...f, next: e.target.value }))} required
              style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '9px 12px', fontSize: 13, boxSizing: 'border-box' }} />
            <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 4 }}>Min 8 characters, one uppercase, one number</div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>CONFIRM NEW PASSWORD</label>
            <input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required
              style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '9px 12px', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          {err && <div style={{ background: '#FEF2F2', color: '#DC2626', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 14 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            {!forced && <button type="button" onClick={onDone} style={{ flex: 1, padding: '10px', background: '#F3F4F6', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#6B7280' }}>Cancel</button>}
            <button type="submit" disabled={saving} style={{ flex: 2, padding: '10px', background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Set Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Client Portal ─────────────────────────────────────────────────────────────
// Exported so ClientPortalShell can render this as its timesheet-approvals
// section. When embedded the shell already supplies the navy header, the
// person, dark mode and sign out, so this drops its own to avoid two.
export function ClientPortal({ contact, onLogout, onSignedOutEverywhere, embedded = false }) {
  const [tab, setTab]               = useState('dashboard');
  const [pendingTs, setPendingTs]   = useState([]);
  const [historyTs, setHistoryTs]   = useState([]);
  const [contracts, setContracts]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [reviewing, setReviewing]   = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionErr, setActionErr]   = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [delegateOptions, setDelegateOptions] = useState([]);
  const [savingDelegateId, setSavingDelegateId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [me, pending, history, rejected, delOpts] = await Promise.all([
        clientApi.get('me'),
        clientApi.get('timesheets?status=pending'),
        clientApi.get('timesheets?status=approved'),
        clientApi.get('timesheets?status=rejected').catch(() => []),
        clientApi.get('delegate-options').catch(() => []),
      ]);
      setContracts(me.contracts || []);
      setPendingTs(pending || []);
      setHistoryTs([...(history || []), ...(rejected || [])].sort((a, b) => b.week_start.localeCompare(a.week_start)));
      setDelegateOptions(delOpts || []);
    } catch { /* non-fatal */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Primary approver sets/changes/clears their own delegate (backup) approver.
  const setDelegate = async (contractId, delegateContactId) => {
    setSavingDelegateId(contractId);
    try {
      await clientApi.put(`contracts/${contractId}/delegate`, { delegate_contact_id: delegateContactId || null });
      await load();
    } catch (e) {
      alert(e.message || 'Could not update the delegate approver.');
    }
    setSavingDelegateId(null);
  };

  const review = async (approvalId, newStatus) => {
    if (newStatus === 'rejected' && !rejectReason.trim()) { setActionErr('Please enter a reason for rejection.'); return; }
    setActionLoading(true); setActionErr('');
    try {
      await clientApi.put(`timesheets/${approvalId}/review`, { status: newStatus, rejection_reason: rejectReason || undefined });
      setReviewing(null); setRejectReason('');
      load();
    } catch (e) { setActionErr(e.message || 'Action failed'); }
    setActionLoading(false);
  };

  const fmt = (n) => n == null ? '—' : (n % 1 === 0 ? String(n) : Number(n).toFixed(1));
  const fmtMoney = (n) => n == null ? '—' : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtLogin = (iso) => {
    if (!iso) return null;
    return new Date(iso.replace(' ', 'T') + 'Z').toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const TABS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'pending',   label: 'Awaiting Approval', badge: pendingTs.length },
    { id: 'history',   label: 'History' },
    { id: 'contracts', label: 'Contractors' },
  ];

  // Dashboard aggregates
  const uniqueWorkers = [...new Map(contracts.map(c => [c.resource_worker_id, c])).values()];
  const totalAgreed   = contracts.reduce((s, c) => s + (parseFloat(c.agreed_days) || 0), 0);
  const totalApproved = contracts.reduce((s, c) => s + (parseFloat(c.days_approved) || 0) + (parseFloat(c.opening_days) || 0), 0);
  const totalPending  = contracts.reduce((s, c) => s + (parseFloat(c.days_pending) || 0), 0);
  const totalExpenses = contracts.reduce((s, c) => s + (parseFloat(c.expenses_approved) || 0), 0);

  // Group contracts by worker for per-person summary
  const workerMap = {};
  for (const c of contracts) {
    if (!workerMap[c.resource_worker_id]) workerMap[c.resource_worker_id] = { name: c.worker_name, email: c.worker_email, contracts: [] };
    workerMap[c.resource_worker_id].contracts.push(c);
  }

  // Group contracts by SOW for contracts tab
  const sowGroups = {};
  for (const c of contracts) {
    const key = c.sow_reference || c.reference || c.id;
    if (!sowGroups[key]) sowGroups[key] = { sow_reference: c.sow_reference, sow_detail: c.sow_detail, contracts: [] };
    sowGroups[key].contracts.push(c);
  }

  if (showChangePw) {
    return <ChangePasswordScreen contact={contact} onDone={() => setShowChangePw(false)} forced={false} />;
  }

  return (
    <div style={embedded ? { fontFamily: 'Montserrat, sans-serif' } : { minHeight: '100vh', background: '#F3F4F6', fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header */}
      {!embedded && (
        <div style={{ background: '#0D1D32', padding: '0 16px' }}>
          <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/av-logo-light.svg" alt="" width="26" height="26" style={{ filter: 'brightness(0) invert(1)' }} />
              <div>
                <div style={{ color: '#E5B87A', fontSize: 12, fontWeight: 800, letterSpacing: 0.5 }}>ALTON VALLEY</div>
                <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 8 }}>Client Portal</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{contact.first_name} {contact.last_name}</div>
                {contact.last_login && (
                  <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 8 }}>Last sign-in: {fmtLogin(contact.last_login)}</div>
                )}
              </div>
              <button onClick={() => setShowChangePw(true)}
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '4px 10px', color: 'rgba(255,255,255,0.5)', fontSize: 9, cursor: 'pointer' }}>
                Change Password
              </button>
              {onSignedOutEverywhere && (
                <SignOutEverywhere endpoint="client/logout-all" onSignedOut={onSignedOutEverywhere}
                  className="bg-white/10 border border-white/10 rounded-md px-2.5 py-1 text-white/50 text-[9px] hover:text-white/80" />
              )}
              <button onClick={onLogout}
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '4px 10px', color: 'rgba(255,255,255,0.5)', fontSize: 9, cursor: 'pointer' }}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab nav */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding: '12px 20px', fontSize: 10, fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer',
                color: tab === t.id ? '#0D1D32' : '#9CA3AF',
                borderBottom: tab === t.id ? '2px solid #A1723D' : '2px solid transparent' }}>
              {t.label}
              {t.badge > 0 && (
                <span style={{ marginLeft: 5, background: '#EF4444', color: '#fff', fontSize: 8, fontWeight: 700, borderRadius: 9, padding: '1px 5px' }}>{t.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        {loading && <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 12 }}>Loading…</div>}

        {/* ── Dashboard ── */}
        {!loading && tab === 'dashboard' && (
          <div>
            {/* Welcome */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0D1D32' }}>Welcome back, {contact.first_name}.</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{contact.client_name || 'Client Portal'} · Timesheet approvals dashboard</div>
            </div>

            {/* Pending reminder */}
            {pendingTs.length > 0 && (
              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E' }}>
                    {pendingTs.length} timesheet{pendingTs.length !== 1 ? 's' : ''} awaiting your approval
                  </div>
                  <div style={{ fontSize: 11, color: '#B45309', marginTop: 2 }}>
                    {pendingTs.slice(0, 3).map(ts => ts.worker_name).join(', ')}{pendingTs.length > 3 ? ` +${pendingTs.length - 3} more` : ''}
                  </div>
                </div>
                <button onClick={() => setTab('pending')}
                  style={{ padding: '8px 16px', background: '#92400E', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Review Now
                </button>
              </div>
            )}

            {/* Summary stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
              {[
                { label: 'Contractors', value: uniqueWorkers.length, color: '#0D1D32' },
                { label: 'Days Agreed', value: fmt(totalAgreed), color: '#374151' },
                { label: 'Days Approved', value: fmt(totalApproved), color: '#16A34A' },
                { label: 'Days Pending', value: fmt(totalPending), color: '#2563EB' },
              ].map(s => (
                <div key={s.label} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '16px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: 26, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {totalExpenses > 0 && (
              <div style={{ background: '#FEF9F0', border: '1px solid #FDE8C8', borderRadius: 10, padding: '12px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#A1723D' }}>{fmtMoney(totalExpenses)}</div>
                <div style={{ fontSize: 11, color: '#92400E' }}>total expenses approved across all contracts</div>
              </div>
            )}

            {/* Per-person breakdown */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#0D1D32' }}>Contractors</span>
                <span style={{ fontSize: 9, color: '#9CA3AF' }}>Agreed · Approved · Pending</span>
              </div>
              {Object.values(workerMap).map((w, i, arr) => {
                const wAgreed   = w.contracts.reduce((s, c) => s + (parseFloat(c.agreed_days) || 0), 0);
                const wApproved = w.contracts.reduce((s, c) => s + (parseFloat(c.days_approved) || 0) + (parseFloat(c.opening_days) || 0), 0);
                const wPending  = w.contracts.reduce((s, c) => s + (parseFloat(c.days_pending) || 0), 0);
                const wExpenses = w.contracts.reduce((s, c) => s + (parseFloat(c.expenses_approved) || 0), 0);
                const pct = wAgreed > 0 ? Math.min(((wApproved + wPending) / wAgreed) * 100, 100) : 0;
                const barColor = pct > 90 ? '#EF4444' : pct > 70 ? '#F59E0B' : '#10B981';
                return (
                  <div key={w.name} style={{ padding: '14px 18px', borderBottom: i < arr.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#0D1D32', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#E5B87A', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                        {(w.name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#0D1D32' }}>{w.name}</div>
                        <div style={{ fontSize: 9, color: '#9CA3AF' }}>{w.contracts.length} contract{w.contracts.length !== 1 ? 's' : ''}{w.contracts[0]?.sow_reference ? ` · ${w.contracts[0].sow_reference}` : ''}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 18, flexShrink: 0 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 15, fontWeight: 900, color: '#0D1D32' }}>{fmt(wAgreed)}</div>
                          <div style={{ fontSize: 7, color: '#9CA3AF' }}>Agreed</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 15, fontWeight: 900, color: '#16A34A' }}>{fmt(wApproved)}</div>
                          <div style={{ fontSize: 7, color: '#9CA3AF' }}>Approved</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 15, fontWeight: 900, color: '#2563EB' }}>{fmt(wPending)}</div>
                          <div style={{ fontSize: 7, color: '#9CA3AF' }}>Pending</div>
                        </div>
                        {wExpenses > 0 && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 13, fontWeight: 900, color: '#A1723D' }}>{fmtMoney(wExpenses)}</div>
                            <div style={{ fontSize: 7, color: '#9CA3AF' }}>Expenses</div>
                          </div>
                        )}
                      </div>
                    </div>
                    {wAgreed > 0 && (
                      <div style={{ background: '#F3F4F6', borderRadius: 4, height: 5 }}>
                        <div style={{ width: `${pct}%`, background: barColor, height: '100%', borderRadius: 4, transition: 'width 0.4s' }} />
                      </div>
                    )}
                  </div>
                );
              })}
              {contracts.length === 0 && (
                <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 11 }}>No contractors assigned to you yet.</div>
              )}
            </div>
          </div>
        )}

        {/* ── Pending Approvals ── */}
        {!loading && tab === 'pending' && (
          <div>
            {pendingTs.length === 0 && (
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>✓</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#374151' }}>All caught up</div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>No timesheets awaiting your approval.</div>
              </div>
            )}
            {pendingTs.map(ts => (
              <div key={ts.approval_id} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#0D1D32', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {ts.worker_name}
                      {ts.acting_as_delegate ? (
                        <span style={{ fontSize: 8, fontWeight: 700, background: '#FEF3C7', color: '#92400E', borderRadius: 4, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: 0.3 }}>Delegate</span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>{ts.sow_reference || ts.contract_reference}{ts.sow_detail ? ` — ${ts.sow_detail}` : ''}</div>
                    <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                      Week of <strong>{fmtShort(ts.week_start)}</strong> · <strong>{fmt((ts.total_hours || 0) / 8)}d</strong> submitted
                    </div>
                  </div>
                  <StatusBadge status={ts.approval_status} />
                </div>
                {ts.entries && ts.entries.length > 0 && (
                  <div style={{ padding: '12px 18px', background: '#F9FAFB', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {DAYS.map((d, i) => {
                      const entry = ts.entries.find(e => e.day === d && e.contract_id === ts.contract_id);
                      const val = entry ? entry.days : 0;
                      return (
                        <div key={d} style={{ textAlign: 'center', minWidth: 36 }}>
                          <div style={{ fontSize: 8, color: '#9CA3AF', marginBottom: 2 }}>{DAY_LABELS[i]}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: val > 0 ? '#0D1D32' : '#9CA3AF' }}>{val}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {ts.timesheet_notes && (
                  <div style={{ padding: '8px 18px', fontSize: 10, color: '#6B7280', borderTop: '1px solid #F3F4F6' }}>Note: {ts.timesheet_notes}</div>
                )}
                <div style={{ padding: '14px 18px', display: 'flex', gap: 8, borderTop: '1px solid #F3F4F6' }}>
                  {reviewing?.approvalId === ts.approval_id ? (
                    <div style={{ width: '100%' }}>
                      <div style={{ marginBottom: 8, fontSize: 12, color: '#374151', fontWeight: 600 }}>Reason for rejection:</div>
                      <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={2}
                        style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 6, padding: '8px 10px', fontSize: 12, boxSizing: 'border-box', marginBottom: 8 }}
                        placeholder="Explain why this timesheet is being rejected…" />
                      {actionErr && <div style={{ color: '#DC2626', fontSize: 10, marginBottom: 6 }}>{actionErr}</div>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => review(ts.approval_id, 'rejected')} disabled={actionLoading}
                          style={{ padding: '8px 16px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          {actionLoading ? 'Rejecting…' : 'Confirm Reject'}
                        </button>
                        <button onClick={() => { setReviewing(null); setRejectReason(''); setActionErr(''); }}
                          style={{ padding: '8px 16px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button onClick={() => review(ts.approval_id, 'approved')} disabled={actionLoading}
                        style={{ padding: '8px 20px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Approve
                      </button>
                      <button onClick={() => { setReviewing({ approvalId: ts.approval_id }); setActionErr(''); }}
                        style={{ padding: '8px 20px', background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── History ── */}
        {!loading && tab === 'history' && (
          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
            {historyTs.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>No historical timesheets yet.</div>
            )}
            {historyTs.map((ts, i) => (
              <div key={ts.approval_id} style={{ padding: '13px 18px', borderBottom: i < historyTs.length - 1 ? '1px solid #F3F4F6' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#0D1D32' }}>{ts.worker_name}</div>
                  <div style={{ fontSize: 10, color: '#6B7280' }}>{ts.sow_reference || ts.contract_reference} · Week of {fmtShort(ts.week_start)}</div>
                  {ts.rejection_reason && (
                    <div style={{ fontSize: 9, color: '#DC2626', marginTop: 2 }}>Reason: {ts.rejection_reason}</div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0D1D32', marginBottom: 3 }}>{fmt((ts.total_hours || 0) / 8)}d</div>
                  <StatusBadge status={ts.approval_status} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Contractors (was Contracts & Drawdown) ── */}
        {!loading && tab === 'contracts' && (
          <div>
            {contracts.length === 0 && (
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
                No contractors assigned to you as approver yet.
              </div>
            )}
            {Object.values(sowGroups).map((group, gi) => (
              <div key={gi} style={{ marginBottom: 16 }}>
                {group.sow_reference && (
                  <div style={{ background: '#0D1D32', borderRadius: '10px 10px 0 0', padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#E5B87A', fontSize: 12, fontWeight: 800 }}>{group.sow_reference}</span>
                    {group.sow_detail && <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>— {group.sow_detail}</span>}
                  </div>
                )}
                <div style={{ border: '1px solid #E5E7EB', borderTop: group.sow_reference ? 'none' : '1px solid #E5E7EB', borderRadius: group.sow_reference ? '0 0 10px 10px' : 10, overflow: 'hidden' }}>
                  {group.contracts.map((c, ci) => {
                    const agreedDays   = parseFloat(c.agreed_days)   || 0;
                    const approvedDays = parseFloat(c.days_approved) || 0;
                    const pendingDays  = parseFloat(c.days_pending)  || 0;
                    const openingDays  = parseFloat(c.opening_days)  || 0;
                    const usedPct      = agreedDays > 0 ? Math.min(((approvedDays + openingDays + pendingDays) / agreedDays) * 100, 100) : 0;
                    const barColor     = usedPct > 90 ? '#EF4444' : usedPct > 70 ? '#F59E0B' : '#10B981';

                    return (
                      <div key={c.id} style={{ background: '#fff', padding: '16px 18px', borderBottom: ci < group.contracts.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: 14, color: '#0D1D32' }}>{c.worker_name}</div>
                            {c.role_description && <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>{c.role_description}</div>}
                            <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 2 }}>{c.reference}</div>
                          </div>
                          {c.pending_approval_count > 0 && (
                            <button onClick={() => setTab('pending')} style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 6, padding: '4px 10px', fontSize: 9, fontWeight: 700, color: '#EA580C', cursor: 'pointer' }}>
                              {c.pending_approval_count} awaiting approval
                            </button>
                          )}
                        </div>

                        {/* Days drawdown bar */}
                        {agreedDays > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#9CA3AF', marginBottom: 5 }}>
                              <span>Days drawdown</span>
                              <span style={{ fontWeight: 700, color: usedPct > 90 ? '#EF4444' : '#6B7280' }}>
                                {fmt(approvedDays + openingDays + pendingDays)} / {fmt(agreedDays)}
                              </span>
                            </div>
                            <div style={{ background: '#F3F4F6', borderRadius: 4, height: 8 }}>
                              <div style={{ width: `${usedPct}%`, background: barColor, height: '100%', borderRadius: 4 }} />
                            </div>
                          </div>
                        )}

                        {/* Stats — no day rate */}
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${c.agreed_expenses != null ? 4 : 3}, 1fr)`, gap: 10, paddingTop: 12, borderTop: '1px solid #F3F4F6' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 18, fontWeight: 900, color: '#0D1D32' }}>{agreedDays > 0 ? fmt(agreedDays) : '—'}</div>
                            <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 3, fontWeight: 600 }}>AGREED DAYS</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 18, fontWeight: 900, color: '#16A34A' }}>{fmt(approvedDays + openingDays)}</div>
                            <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 3, fontWeight: 600 }}>APPROVED</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 18, fontWeight: 900, color: '#2563EB' }}>{fmt(pendingDays)}</div>
                            <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 3, fontWeight: 600 }}>PENDING</div>
                          </div>
                          {c.agreed_expenses != null && (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 15, fontWeight: 900, color: '#A1723D' }}>{fmtMoney(c.expenses_approved)}</div>
                              <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 3, fontWeight: 600 }}>EXPENSES APPROVED</div>
                              {parseFloat(c.agreed_expenses) > 0 && (
                                <div style={{ fontSize: 7, color: '#D1D5DB' }}>budget {fmtMoney(c.agreed_expenses)}</div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Delegate (backup) approver — only the primary approver may set it */}
                        {c.is_primary_approver_for_me && (
                          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #F3F4F6' }}>
                            <div style={{ fontSize: 9, color: '#9CA3AF', fontWeight: 700, marginBottom: 5 }}>DELEGATE (BACKUP) APPROVER</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <select
                                value={c.delegate_approver_type === 'external' ? (c.delegate_approver_id || '') : ''}
                                disabled={savingDelegateId === c.id}
                                onChange={e => setDelegate(c.id, e.target.value)}
                                style={{ flex: 1, minWidth: 180, padding: '8px 10px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6, background: '#fff', color: '#0D1D32' }}>
                                <option value="">— No delegate —</option>
                                {delegateOptions.map(o => (
                                  <option key={o.id} value={o.id}>{o.first_name} {o.last_name}{o.role ? ` · ${o.role}` : ''}</option>
                                ))}
                              </select>
                              {savingDelegateId === c.id && <span style={{ fontSize: 10, color: '#9CA3AF' }}>Saving…</span>}
                            </div>
                            {c.delegate_approver_type === 'internal' && c.delegate_name && (
                              <div style={{ fontSize: 9, color: '#B45309', marginTop: 5 }}>
                                Currently <strong>{c.delegate_name}</strong> (Alton Valley) is your delegate. Choosing someone above replaces them.
                              </div>
                            )}
                            <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 5 }}>
                              Your delegate can approve timesheets on your behalf when you're unavailable. You can't select yourself.
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Portal ───────────────────────────────────────────────────────────────
export default function CasualPortal({ portalType = 'resource' }) {
  const isClientPortal = portalType === 'client';
  const [worker, setWorker]       = useState(null);
  const [checking, setChecking]   = useState(true);
  const [mustChangePw, setMustChangePw] = useState(false);
  const [tab, setTab]             = useState('home');
  const [timesheets, setTimesheets] = useState([]);
  const [invoices, setInvoices]   = useState([]);
  const [signedOutNotice, setSignedOutNotice] = useState('');

  // A request anywhere in the portal found the session had run out: back to
  // sign-in, saying why.
  useEffect(() => {
    const ended = (e) => { setWorker(null); setMustChangePw(false); setSignedOutNotice(e.detail || ''); };
    window.addEventListener(SESSION_ENDED_EVENT, ended);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, ended);
  }, []);

  useEffect(() => {
    if (isClientPortal) {
      // Client portal — only check client session
      clientApi.get('me')
        .then(c => { if (c?.authenticated === false) { setSignedOutNotice(endedNotice(c)); setChecking(false); return; } setWorker({ ...c, userType: 'client' }); setChecking(false); })
        .catch(() => setChecking(false));
    } else {
      // Resource portal — try worker session first, then client session
      casApi.get('me')
        .then(w => {
          if (w?.authenticated === false) {
            // No resource session — check for client session
            clientApi.get('me')
              .then(c => { if (c?.authenticated === false) { setSignedOutNotice(endedNotice(w) || endedNotice(c)); setChecking(false); return; } setWorker({ ...c, userType: 'client' }); setChecking(false); })
              .catch(() => setChecking(false));
          } else {
            setWorker({ ...w, userType: 'worker' });
            if (w.must_change_password) setMustChangePw(true);
            setChecking(false);
          }
        })
        .catch(() => {
          clientApi.get('me')
            .then(c => { if (c?.authenticated === false) { setChecking(false); return; } setWorker({ ...c, userType: 'client' }); setChecking(false); })
            .catch(() => setChecking(false));
        });
    }
  }, [isClientPortal]);

  const loadData = useCallback(() => {
    if (!worker || worker.userType === 'client') return;
    Promise.all([
      casApi.get('timesheets?limit=52'),
      casApi.get('invoices'),
    ]).then(([ts, inv]) => {
      setTimesheets(ts || []);
      setInvoices(inv || []);
    }).catch(() => {});
  }, [worker]);

  useEffect(() => { if (worker) loadData(); }, [worker, loadData]);

  const handleLogin = async (basicUser) => {
    const mustChange = basicUser?.must_change_password || false;
    if (basicUser?.userType === 'client') {
      try { setWorker({ ...await clientApi.get('me'), userType: 'client' }); } catch { setWorker(basicUser); }
      setMustChangePw(mustChange);
    } else {
      try {
        const me = await casApi.get('me');
        setWorker({ ...me, userType: 'worker' });
        setMustChangePw(mustChange || !!me.must_change_password);
      } catch { setWorker(basicUser); setMustChangePw(mustChange); }
    }
  };

  const handleLogout = async () => {
    if (worker?.userType === 'client') {
      await clientApi.post('logout', {}).catch(() => {});
    } else {
      await casApi.post('logout', {}).catch(() => {});
    }
    setWorker(null);
  };

  // Every session ended, this one included: back to sign-in, saying so.
  const handleSignedOutEverywhere = (notice) => {
    setWorker(null);
    setMustChangePw(false);
    setSignedOutNotice(notice);
  };

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', background: '#0D1D32', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif' }}>
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>Loading…</div>
      </div>
    );
  }

  if (!worker) return <LoginScreen onLogin={handleLogin} portalType={portalType} notice={signedOutNotice} />;

  // Client contact portal — completely different view
  if (worker.userType === 'client') {
    if (mustChangePw) {
      return <ChangePasswordScreen contact={worker} forced onDone={() => setMustChangePw(false)} />;
    }
    return <ClientPortal contact={worker} onLogout={handleLogout} onSignedOutEverywhere={handleSignedOutEverywhere} />;
  }

  // Contractor forced to set their own password on first login (welcome email /
  // admin reset). Block the portal until they do.
  if (mustChangePw) {
    return <ChangePasswordScreen contact={worker} forced portalType="resource"
      onDone={async () => {
        setMustChangePw(false);
        try { setWorker({ ...await casApi.get('me'), userType: 'worker' }); } catch { /* keep current */ }
      }} />;
  }

  const pendingCount = timesheets.filter(ts => ts.status === 'submitted').length;

  const TABS = [
    { id: 'home',      label: 'Home',      icon: '⌂' },
    { id: 'timesheet', label: 'Timesheet', icon: '📅' },
    { id: 'invoice',   label: 'Invoice',   icon: '🧾' },
    { id: 'history',   label: 'History',   icon: '📋' },
    { id: 'account',   label: 'Account',   icon: '👤' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#F3F4F6', fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header */}
      <div style={{ background: '#0D1D32', padding: '0 16px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 52 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/av-logo-light.svg" alt="" width="24" height="24" style={{ filter: 'brightness(0) invert(1)' }} />
            <div>
              <div style={{ color: '#E5B87A', fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>ALTON VALLEY</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>{worker.first_name} {worker.last_name}</div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9 }}>{worker.username}</div>
            </div>
            <button onClick={handleLogout}
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '4px 10px', color: 'rgba(255,255,255,0.45)', fontSize: 9, cursor: 'pointer' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Tab nav */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ flex: 1, padding: '8px 0 6px', border: 'none', background: 'none', cursor: 'pointer', position: 'relative',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                color: tab === t.id ? '#0D1D32' : '#9CA3AF',
                borderBottom: tab === t.id ? '2px solid #0D1D32' : '2px solid transparent' }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>{t.icon}</span>
              <span style={{ fontSize: 9, fontWeight: 700 }}>{t.label}</span>
              {t.id === 'timesheet' && pendingCount > 0 && (
                <span style={{ position: 'absolute', top: 6, right: '18%', width: 8, height: 8, background: '#EF4444', borderRadius: '50%' }} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 16px' }}>
        {tab === 'home'      && <DashboardView worker={worker} timesheets={timesheets} invoices={invoices} onNavigate={setTab} />}
        {tab === 'timesheet' && <TimesheetView worker={worker} onRefresh={loadData} />}
        {tab === 'invoice'   && <InvoiceView   worker={worker} invoices={invoices} onRefresh={loadData} />}
        {tab === 'history'   && <HistoryView   timesheets={timesheets} invoices={invoices} />}
        {tab === 'account'   && <AccountView   worker={worker} onSignedOutEverywhere={handleSignedOutEverywhere} />}
      </div>
    </div>
  );
}
